import {
  ReferrerSource,
  REFERRER_SOURCE,
  VXB,
} from "@capone/common";
import { useExperimentIsVariant, useExperiments } from "@capone/experiments";
import { useDeviceTypes } from "halifax";
import { useState, useEffect, useRef } from "react";
import {
  CorpBusinessAccount,
  CorporateInfo,
  ModalNames,
  ModalType,
  RewardsAccount,
} from "redmond";
import { fetchAreModalsEligible } from "b2b-base/src/api/v1/user/fetchAreModalsEligible";
import { isHomepageEntryPath } from "../../../../utils/homepageEntryPaths";
import { useShowMultiAccountSwitcherModal } from "../../MultiAccount/useShowMultiAccountSwitcherModal";

const MODAL_PRIORITY: ModalType[] = [
  "multiaccount",
  "onboarding",
  "postBookingFDA",
  "seanEllis",
  "sameDayRoundTrip",
];

const initialModalState = {
  multiaccount: null,
  onboarding: null,
  // TODO: add postBookingFDA conditions to active modal manager
  postBookingFDA: false,
  seanEllis: null,
  sameDayRoundTrip: null,
};

type ModalState = Record<ModalType, boolean | null>;

export const useActiveModalManager = (
  corporateInfo: CorporateInfo | undefined,
  currentAccount: RewardsAccount | undefined,
  businessAccounts: CorpBusinessAccount[],
  pathname: string,
  isAccountPreselectedOnBackend?: boolean
) => {
  const [activeModal, setActiveModal] = useState<ModalType | null>(() => null);
  const [modalConditions, setModalConditions] =
    useState<ModalState>(initialModalState);

  const dismissedModals = useRef<Set<ModalType>>(new Set());

  const { matchesMobile } = useDeviceTypes();

  const { experimentsCallState } = useExperiments();

  const isSeanEllisQuestionEnabled = useExperimentIsVariant(
    "corp-sean-ellis-question",
    "available"
  );

  const isSeanEllisQuestionDebugEnabled = useExperimentIsVariant(
    "corp-sean-ellis-question",
    "debug"
  );

  const isOnboardingDebugEnabled = useExperimentIsVariant(
    "corp-onboarding-revamp",
    "debug"
  );

  const isSameDayRoundTripEnabled = useExperimentIsVariant(
    "c1-single-day-round-trip-flights",
    "available"
  );

  const { hasSeenMultiAccountsSwitcherModal } =
    useShowMultiAccountSwitcherModal();

  const toggleModal = (modal: ModalType, condition: boolean) =>
    setModalConditions((prev) => ({ ...prev, [modal]: condition }));

  // note: determine multiaccount modal eligibility
  useEffect(() => {
    if (!businessAccounts.length) {
      return;
    }

    const referrerSource = new URLSearchParams(window.location?.search).get(
      REFERRER_SOURCE
    );

    const isAccountPreSelected =
      referrerSource === ReferrerSource.AdminPortal ||
      isAccountPreselectedOnBackend;

    if (isAccountPreSelected) {
      toggleModal("multiaccount", false);
      return;
    }

    const showMultiAccountModal =
      isHomepageEntryPath(pathname) &&
      !dismissedModals.current.has("multiaccount") &&
      !hasSeenMultiAccountsSwitcherModal &&
      businessAccounts.length > 1;

    toggleModal("multiaccount", showMultiAccountModal);
  }, [
    businessAccounts,
    hasSeenMultiAccountsSwitcherModal,
    pathname,
    isAccountPreselectedOnBackend,
  ]);

  // note: determine onboarding modal and vxb helptip eligibility
  useEffect(() => {
    if (!corporateInfo) {
      return;
    }

    const hasSeenOnboardingModal =
      corporateInfo.hasSeenModalMap?.[ModalNames.HAS_SEEN_ONBOARDING_MODAL];

    const hasSeenVxbOnboardingModal =
      corporateInfo.hasSeenModalMap?.[ModalNames.HAS_SEEN_VXB_ONBOARDING_MODAL];

    const isVXB = currentAccount?.productDisplayName === VXB;

    const isNFU = currentAccount?.customerAccountRole === "Non Financial User";

    const shouldShowVxbOnboarding =
      isVXB &&
      !hasSeenVxbOnboardingModal &&
      !dismissedModals.current.has("onboarding");
    const shouldShowSparkOnboarding =
      !hasSeenOnboardingModal &&
      !matchesMobile &&
      !isVXB &&
      !dismissedModals.current.has("onboarding");
    const shouldShowNFUOnboarding = isNFU && !hasSeenOnboardingModal && !hasSeenVxbOnboardingModal;

    const showOnboardingModal =
      isOnboardingDebugEnabled ||
      shouldShowVxbOnboarding ||
      shouldShowSparkOnboarding ||
      shouldShowNFUOnboarding;

    setModalConditions((prev) => ({
      ...prev,
      onboarding: showOnboardingModal,
    }));
  }, [
    corporateInfo,
    currentAccount,
    matchesMobile,
    isOnboardingDebugEnabled,
  ]);

  // note: determine Sean Ellis question modal eligibility
  useEffect(() => {
    if (!corporateInfo) {
      return;
    }

    const hasSeenSeanEllisQuestion = corporateInfo.hasSeenModalMap?.sean_ellis;

    const checkSeanEllisModalEligibility = async () => {
      const eligibilityMap = await fetchAreModalsEligible(["sean_ellis"]);
      const isModalEligible = eligibilityMap.sean_ellis;
      toggleModal("seanEllis", isModalEligible);
    };

    if (isSeanEllisQuestionDebugEnabled) {
      toggleModal("seanEllis", true);
    } else if (
      !matchesMobile &&
      !hasSeenSeanEllisQuestion &&
      isSeanEllisQuestionEnabled
    ) {
      checkSeanEllisModalEligibility();
    } else {
      toggleModal("seanEllis", false);
    }
  }, [
    corporateInfo,
    isSeanEllisQuestionEnabled,
    isSeanEllisQuestionDebugEnabled,
    matchesMobile,
  ]);

  useEffect(() => {
    if (!corporateInfo) {
      return;
    }

    const hasSeenSameDayRoundTripModal =
      corporateInfo.hasSeenModalMap?.[ModalNames.SAME_DAY_ROUND_TRIP_MODAL];

    const isSameDayRoundTripModalEligible =
      isSameDayRoundTripEnabled &&
      !corporateInfo.isFirstCorporateSession &&
      !hasSeenSameDayRoundTripModal &&
      !dismissedModals.current.has("sameDayRoundTrip");

    toggleModal("sameDayRoundTrip", isSameDayRoundTripModalEligible);
  }, [corporateInfo, isSameDayRoundTripEnabled]);

  useEffect(() => {
    if (
      experimentsCallState === "loading" ||
      Object.values(modalConditions).some((condition) => condition === null)
    ) {
      return;
    }

    // note: determine the highest-priority modal to show
    const nextModal =
      MODAL_PRIORITY.find((modal) => modalConditions[modal]) || null;
    setTimeout(() => {
      setActiveModal(nextModal);
    }, 1000);
  }, [modalConditions, experimentsCallState]);

  const closeActiveModal = (modal: ModalType) => {
    dismissedModals.current.add(modal);
    setActiveModal(null);
    setModalConditions((prev) => ({
      ...prev,
      [modal]: false,
    }));
  };

  return { activeModal, closeActiveModal };
};
